import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import { ArrowLeft, Sparkles, Save, History, Loader2 } from "lucide-react";
import { API, api, formatApiError, tokenStore } from "../api/client";
import { useAuth } from "../store/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import SubmitForReviewModal from "../components/SubmitForReviewModal";
import VersionsModal from "../components/VersionsModal";
import DocumentSidebar from "../components/DocumentSidebar";

const STATUS_LABELS = { draft: "Borrador", review: "En revisión", approved: "Aprobado", obsolete: "Obsoleto" };
const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-700",
  review: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  obsolete: "bg-slate-100 text-slate-400 line-through",
};

export default function DocumentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [related, setRelated] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [savingState, setSavingState] = useState(""); // '', 'saving', 'saved', 'error'
  const [aiOpen, setAiOpen] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [addCtrlOpen, setAddCtrlOpen] = useState(false);
  const [newCtrl, setNewCtrl] = useState("");
  const editorRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/documents/${id}`);
        if (cancelled) return;
        setDoc(data);
        setTitle(data.title || "");
        setContent(data.content || "");
        setRelated(data.related_controls || []);
        setDirty(false);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        navigate("/documents");
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const isOwner = user.role === "owner" || user.role === "admin";
  const editable = !!doc && (doc.status === "draft" || (doc.status === "review" && (doc.reviewer_user_id === user.id || isOwner)));

  const save = useCallback(async (silent = false) => {
    if (!doc) return;
    if (!editable) return;
    setSavingState("saving");
    try {
      const payload = { title, content, related_controls: related };
      const { data } = await api.patch(`/documents/${id}`, payload);
      setDoc(data); setDirty(false);
      setSavingState("saved");
      if (!silent) toast.success("Cambios guardados.");
    } catch (err) {
      setSavingState("error");
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, [doc, title, content, related, id, editable]);

  // Aviso de cambios sin guardar al cerrar/recargar (evita pérdida de datos sin autosave temporizado).
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  if (!doc) return <div className="text-slate-500">Cargando documento...</div>;

  const canSubmit = doc.status === "draft" && (doc.created_by_user_id === user.id || isOwner) && !["auditor", "member"].includes(user.role);
  const canApproveReject = doc.status === "review" && (doc.reviewer_user_id === user.id || isOwner);
  const canObsolete = doc.status === "approved" && (isOwner || doc.approved_by_user_id === user.id);
  const canExportPdf = doc.status === "approved";

  const onChangeContent = (v) => { setContent(v || ""); setDirty(true); };
  const onChangeTitle = (v) => { setTitle(v); setDirty(true); };

  const captureSelection = () => {
    try {
      const sel = window.getSelection();
      if (sel && sel.toString().trim()) setSelectedText(sel.toString());
    } catch {
      /* sin selección disponible */
    }
  };

  const insertAfterSelection = (text) => {
    let newContent;
    if (selectedText && content.includes(selectedText)) {
      newContent = content.replace(selectedText, `${selectedText}\n\n${text}`);
    } else {
      newContent = `${content}\n\n${text}`;
    }
    setContent(newContent); setDirty(true);
  };

  const replaceSelection = (text) => {
    if (!selectedText) return insertAfterSelection(text);
    const newContent = content.replace(selectedText, text);
    setContent(newContent); setDirty(true); setSelectedText("");
  };

  const openVersions = async () => {
    const { data } = await api.get(`/documents/${id}/versions`);
    setVersions(data); setVersionsOpen(true);
  };

  const approve = async () => {
    try { const { data } = await api.post(`/documents/${id}/approve`); setDoc(data); toast.success("Documento aprobado."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) return toast.error("Indica un motivo.");
    try {
      const { data } = await api.post(`/documents/${id}/reject`, { rejection_reason: rejectReason });
      setDoc(data); setRejectOpen(false); setRejectReason("");
      toast.success("Documento rechazado.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const obsolete = async () => {
    if (!window.confirm("¿Marcar el documento como obsoleto?")) return;
    try { const { data } = await api.post(`/documents/${id}/mark-obsolete`); setDoc(data); toast.success("Marcado como obsoleto."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadPdf = async () => {
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API}/documents/${id}/export.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("No se pudo descargar el PDF.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${title.replace(/ /g, "_")}_v${doc.current_version}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const addControl = () => {
    const code = newCtrl.trim().toUpperCase();
    if (!code) return;
    if (related.includes(code)) return toast.info("Ya está añadido.");
    setRelated([...related, code]); setDirty(true);
    setNewCtrl(""); setAddCtrlOpen(false);
  };

  const removeControl = (c) => { setRelated(related.filter((x) => x !== c)); setDirty(true); };

  return (
    <div data-testid="document-editor-page" className="max-w-7xl">
      <Button variant="ghost" onClick={() => navigate("/documents")} className="mb-3"><ArrowLeft className="h-4 w-4 mr-1" /> Documentos</Button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Input
              value={title}
              disabled={!editable}
              onChange={(e) => onChangeTitle(e.target.value)}
              className="text-[18px] font-semibold border-0 px-0 focus-visible:ring-0 shadow-none"
              data-testid="doc-title"
            />
            <Badge className={STATUS_CLS[doc.status]}>{STATUS_LABELS[doc.status]}</Badge>
            <span className="text-[12px] text-slate-400">v{doc.current_version}</span>
            <div className="flex-1" />
            {savingState === "saving" && <span className="text-[12px] text-slate-500"><Loader2 className="h-3 w-3 mr-1 inline animate-spin" /> Guardando...</span>}
            {savingState === "saved" && <span className="text-[12px] text-emerald-600">Guardado</span>}
            <Button variant="outline" size="sm" onClick={openVersions} data-testid="btn-versions"><History className="h-4 w-4 mr-1" /> Versiones</Button>
            {editable && (
              <Button variant="outline" size="sm" onClick={() => { captureSelection(); setAiOpen(true); }} data-testid="btn-ai">
                <Sparkles className="h-4 w-4 mr-1" /> Asistente IA
              </Button>
            )}
          </div>

          <div data-color-mode="light" onMouseUp={captureSelection} ref={editorRef}>
            <MDEditor
              value={content}
              onChange={onChangeContent}
              height={620}
              preview={editable ? "live" : "preview"}
              data-testid="md-editor"
            />
          </div>

          {dirty && editable && (
            <Button className="fixed bottom-6 right-6 shadow-lg" onClick={() => save(false)} data-testid="btn-save">
              <Save className="h-4 w-4 mr-1" /> Guardar cambios
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {doc.review_guide?.length > 0 && ["draft", "review"].includes(doc.status) && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="doc-review-guide">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-amber-900 mb-2">
                <Sparkles className="h-4 w-4" /> Antes de aprobar, comprueba
              </div>
              <ul className="list-disc pl-5 space-y-1.5 text-[13px] text-amber-900/90">
                {doc.review_guide.map((g, i) => <li key={i}>{g}</li>)}
              </ul>
              <p className="text-[11px] text-amber-700/80 mt-2">Este borrador lo redactó la IA. Lo apruebas tú, no se aprueba solo.</p>
            </div>
          )}
          <DocumentSidebar
            doc={doc}
            user={user}
            related={related}
            editable={editable}
            docId={id}
            selectedText={selectedText}
            permissions={{ canSubmit, canApproveReject, canExportPdf, canObsolete }}
            aiOpen={aiOpen}
            onCloseAi={() => setAiOpen(false)}
            onInsert={insertAfterSelection}
            onReplace={replaceSelection}
            onOpenSubmit={() => setSubmitOpen(true)}
            onApprove={approve}
            onOpenReject={() => setRejectOpen(true)}
            onDownloadPdf={downloadPdf}
            onObsolete={obsolete}
            onOpenAddControl={() => setAddCtrlOpen(true)}
            onRemoveControl={removeControl}
          />
        </div>
      </div>

      {submitOpen && <SubmitForReviewModal open={submitOpen} onClose={() => setSubmitOpen(false)} doc={doc} onDone={(d) => { setDoc(d); setSubmitOpen(false); }} />}
      {versionsOpen && <VersionsModal open={versionsOpen} onClose={() => setVersionsOpen(false)} docId={id} versions={versions} />}

      <Dialog open={rejectOpen} onOpenChange={(o) => !o && setRejectOpen(false)}>
        <DialogContent data-testid="modal-reject">
          <DialogHeader><DialogTitle>Rechazar documento</DialogTitle></DialogHeader>
          <Textarea placeholder="Motivo del rechazo..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={4} data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancelar</Button>
            <Button onClick={reject} data-testid="reject-submit">Rechazar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addCtrlOpen} onOpenChange={(o) => !o && setAddCtrlOpen(false)}>
        <DialogContent data-testid="modal-add-control">
          <DialogHeader><DialogTitle>Añadir control</DialogTitle></DialogHeader>
          <Input placeholder="Ej. A.5.1" value={newCtrl} onChange={(e) => setNewCtrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addControl()} data-testid="add-ctrl-input" autoFocus />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddCtrlOpen(false)}>Cancelar</Button>
            <Button onClick={addControl} data-testid="add-ctrl-submit">Añadir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
